import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle, Clock, X } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";

const ID_TYPES = [
  { v: "aadhaar", l: "Aadhaar Card" },
  { v: "pan", l: "PAN Card" },
  { v: "driving_licence", l: "Driving Licence" },
];

export default function SellerVerification() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    id_type: "aadhaar",
    id_number: "",
    id_front_url: "",
    id_back_url: "",
    selfie_url: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/seller/verification")
      .then(({ data }) => setState(data))
      .catch((err) => console.warn("verification load failed", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.id_number.trim()) return toast.error("Enter ID number");
    if (!form.id_front_url) return toast.error("Upload front of ID");
    if (!form.selfie_url) return toast.error("Upload a selfie holding your ID");
    setSubmitting(true);
    try {
      await api.post("/seller/verification", form);
      toast.success("Submitted — we'll review in 24-48h");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <p className="font-mono-b p-6" data-testid="kyc-loading">Loading verification...</p>;

  const status = state?.status || "unsubmitted";

  const statusUI = {
    approved:  { icon: ShieldCheck, cls: "bg-[#D4FF00] border-black", label: "VERIFIED SELLER", note: "You're cleared to list on OUTLAW-D. Trust badge is live on your listings." },
    pending:   { icon: Clock,       cls: "bg-black text-[#D4FF00] border-black", label: "UNDER REVIEW", note: "Our team is reviewing your documents. Usually takes 24-48h." },
    rejected:  { icon: AlertTriangle, cls: "bg-[#FF007F] text-white border-black", label: "REJECTED", note: state?.notes || "Please re-submit with clearer photos." },
    unsubmitted: { icon: AlertTriangle, cls: "bg-[#F4F4F0] border-black", label: "NOT SUBMITTED", note: "Fill the form below to submit your ID + selfie for review." },
  }[status];

  return (
    <div className="space-y-6" data-testid="kyc-page">
      {statusUI && (
        <div className={`border-2 brutal-shadow-sm p-4 flex items-start gap-3 ${statusUI.cls}`} data-testid="kyc-status">
          <statusUI.icon className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-display text-2xl leading-none">{statusUI.label}</p>
            <p className="font-mono-b text-xs mt-1">{statusUI.note}</p>
          </div>
        </div>
      )}

      {(status === "unsubmitted" || status === "rejected") && (
        <form onSubmit={submit} className="bg-white border-2 border-black brutal-shadow p-6 space-y-5" data-testid="kyc-form">
          <div>
            <h3 className="font-display text-3xl">GET VERIFIED · UNLOCK LISTING</h3>
            <p className="font-mono-b text-sm text-black/70 mt-1">
              To keep OUTLAW-D safe, every seller submits a government ID + selfie. Manually reviewed by our team. Docs are only visible to admins.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-mono-b font-bold uppercase">ID TYPE</label>
              <select
                data-testid="kyc-id-type"
                value={form.id_type}
                onChange={(e) => setForm({ ...form, id_type: e.target.value })}
                className="brutal-input w-full mt-1 uppercase"
              >
                {ID_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-mono-b font-bold uppercase">ID NUMBER</label>
              <input
                data-testid="kyc-id-number"
                value={form.id_number}
                onChange={(e) => setForm({ ...form, id_number: e.target.value })}
                className="brutal-input w-full mt-1"
                placeholder="e.g. XXXX-XXXX-1234"
                required
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-mono-b font-bold uppercase block mb-2">ID FRONT PHOTO *</label>
            <ImageUpload
              value={form.id_front_url ? [form.id_front_url] : []}
              onChange={(imgs) => setForm({ ...form, id_front_url: imgs[0] || "" })}
              max={1}
              testIdPrefix="kyc-front"
            />
          </div>

          <div>
            <label className="text-xs font-mono-b font-bold uppercase block mb-2">ID BACK PHOTO (optional)</label>
            <ImageUpload
              value={form.id_back_url ? [form.id_back_url] : []}
              onChange={(imgs) => setForm({ ...form, id_back_url: imgs[0] || "" })}
              max={1}
              testIdPrefix="kyc-back"
            />
          </div>

          <div>
            <label className="text-xs font-mono-b font-bold uppercase block mb-2">SELFIE HOLDING YOUR ID *</label>
            <p className="font-mono-b text-[11px] text-black/60 mb-2">Hold your ID next to your face. Face + ID must be clearly visible.</p>
            <ImageUpload
              value={form.selfie_url ? [form.selfie_url] : []}
              onChange={(imgs) => setForm({ ...form, selfie_url: imgs[0] || "" })}
              max={1}
              testIdPrefix="kyc-selfie"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="kyc-submit"
            className="bg-[#D4FF00] border-2 border-black brutal-shadow hover-nudge px-6 py-3 font-mono-b font-bold uppercase disabled:opacity-50"
          >
            {submitting ? "SUBMITTING..." : "SUBMIT FOR VERIFICATION"}
          </button>
        </form>
      )}

      {status === "approved" && state && (
        <div className="border-2 border-black bg-white brutal-shadow-sm p-4" data-testid="kyc-approved-info">
          <p className="font-mono-b text-xs text-black/60">SUBMITTED</p>
          <p className="font-mono-b text-sm">{new Date(state.created_at).toLocaleString()}</p>
          <p className="font-mono-b text-xs text-black/60 mt-3">ID TYPE</p>
          <p className="font-mono-b text-sm uppercase">{state.id_type?.replace("_", " ")}</p>
        </div>
      )}
    </div>
  );
}
